import {
  Avatar,
  Button,
  ButtonProps,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import {
  EmphasizedNumber,
  IconButton,
  InformationModal,
  ItemImageArea,
} from 'utoniq-ui'

import {
  ItemSerialCountDetailFragment,
  Maybe,
  MultiItemSerialDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, BottomSafeArea, Head } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { styleSets } from '@/components/styles'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { setRedirectAfterLoginPath } from '@/core/native/storage'

import {
  GetItemMultiCompletionModal,
  itemMultiCompletionModalRef,
} from './GetItemMultiCompletionModal'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  isLoggedIn: boolean
  isAcquired: boolean
  canBeObtain: boolean
  showCompletedScreen: boolean
  multiItemId: string
  gettingItemMulti: boolean
  multiItemSerial: Maybe<MultiItemSerialDetailFragment>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onGetItemMulti: () => Promise<void>
}

export const QRcodeGetItemMultiTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onGetItemMulti,
}) => {
  const { t } = useTranslation('qrCodeGetItemMulti')
  const { router, routerPages, routerNames } = useRouter()
  return (
    <BaseLayout
      margin
      header={{
        left: (
          <IconButton.Close
            edge="start"
            size="medium"
            onClick={() => router.push(routerPages.GlobalHome)}
          />
        ),
        title: t('title'),
        type: 'modal',
      }}
      customNavigation={
        state.loading ? null : (
          <FixedButtonCell
            multiItemId={state.multiItemId}
            isLoggedIn={state.isLoggedIn}
            isAcquired={state.isAcquired}
            canBeObtain={state.canBeObtain}
            gettingItemMulti={state.gettingItemMulti}
            onGetItemMulti={onGetItemMulti}
          />
        )
      }
    >
      <Head title={routerNames.QRcodeGetItemMulti} />
      <ScrollFetcher
        loading={state.loading}
        nodes={state.multiItemSerial?.items?.map(itemSerial => (
          <ItemBoxCell itemSerial={itemSerial} />
        ))}
        componentProps={{ spacing: 2 }}
        hasNextPage={state.hasNextPage}
        onFetchMore={onFetchMore}
        loadingComponent={
          <Stack>
            {Array.from({ length: 7 }).map((_, index) => (
              <ItemBoxSkeletonCell key={index} />
            ))}
          </Stack>
        }
      />
      <GetItemMultiCompletionModal
        loading={state.loading}
        open={state.showCompletedScreen}
        items={state.multiItemSerial?.items}
      />
    </BaseLayout>
  )
}

type ItemBoxProps = {
  itemSerial: ItemSerialCountDetailFragment
}
const ItemBoxCell: Cell<ItemBoxProps> = ({ itemSerial }) => {
  const { t } = useTranslation('qrCodeGetItemMulti')
  const { count, item, sku } = itemSerial
  return (
    <CellFrame>
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
          <Stack direction="row" spacing={4}>
            <ItemImageArea
              src={item.thumbnail?.front?.smallUri ?? ''}
              size="x-small"
            />
            <Stack flex={1} justifyContent="space-between">
              <Stack spacing={2}>
                <Typography variant="h5" sx={styleSets.lineCamp(2)}>
                  {item.title}
                </Typography>
                {sku?.unitName && (
                  <Typography variant="body2">{sku.unitName}</Typography>
                )}
              </Stack>
              {item.rarity && (
                <Stack direction="row" spacing={1} alignItems="center">
                  {item?.rarityIconImage?.smallUri && (
                    <Avatar
                      variant="square"
                      src={item.rarityIconImage.smallUri}
                      sx={{ height: 12, width: 12 }}
                    />
                  )}
                  <Typography variant="subtitle2" color="text.secondary">
                    {item?.rarity?.name ? t(item?.rarity.name) : '-'}
                  </Typography>
                </Stack>
              )}
            </Stack>
            <Stack alignItems="center" justifyContent="center">
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <EmphasizedNumber variant="h1">{count}</EmphasizedNumber>
                <Typography variant="subtitle1">{t('sheet', '枚')}</Typography>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </CellFrame>
  )
}

const ItemBoxSkeletonCell: Cell = () => (
  <CellFrame sx={{ py: 3 }}>
    <Stack direction="row" spacing="34px">
      <Skeleton variant="rectangular" width={85} height={68} />
      <Stack spacing={3} flex={1}>
        <Skeleton variant="rectangular" width="100%" height={15} />
        <Skeleton variant="rectangular" width="100%" height={15} />
        <Stack direction="row" justifyContent="space-between">
          <Skeleton variant="rectangular" width="50%" height={15} />
          <Skeleton variant="rectangular" width="15%" height={15} />
        </Stack>
      </Stack>
    </Stack>
  </CellFrame>
)

type FixedButtonCellProps = {
  isLoggedIn: boolean
  isAcquired: boolean
  multiItemId: string
  canBeObtain: boolean
  gettingItemMulti: boolean
  onGetItemMulti: Action['onGetItemMulti']
}
const FixedButtonCell: Cell<FixedButtonCellProps> = ({
  isLoggedIn,
  isAcquired,
  multiItemId,
  canBeObtain,
  gettingItemMulti,
  onGetItemMulti,
}) => {
  const { t } = useTranslation('qrCodeGetItemMulti')
  const { showError } = useMessage()
  const { router, generateQueryPath, routerPages, generatePath } = useRouter()
  const [openConfirm, setOpenConfirm] = useState<boolean>(false)

  const { label, disabled, variant, onClick } = useMemo<{
    label: string
    disabled?: boolean
    variant?: ButtonProps['variant']
    onClick?: () => void
  }>(() => {
    if (!isLoggedIn) {
      return {
        label: t('registerMember', '会員登録してアイテムを獲得する'),
        variant: 'text',
        onClick() {
          setRedirectAfterLoginPath(router.pathname)
          return router.push(routerPages.Top)
        },
      }
    }
    if (isAcquired) {
      return {
        label: t('alreadyAcquired', '獲得済み'),
        variant: undefined,
        disabled: true,
      }
    }
    if (!canBeObtain) {
      return {
        label: t('currentUnavailable', '現在、獲得できません'),
        variant: undefined,
        disabled: true,
      }
    }
    return {
      label: t('acquireItem', 'アイテムを獲得する'),
      variant: undefined,
      onClick() {
        setOpenConfirm(true)
      },
    }
  }, [
    isLoggedIn,
    isAcquired,
    canBeObtain,
    t,
    router,
    generateQueryPath,
    routerPages.Login,
  ])

  const handleAcquireItem = useCallback(async () => {
    await onGetItemMulti()
      .then(() => {
        setOpenConfirm(false)
        router.replace(
          generatePath(routerPages.QRcodeGetItemMulti, {
            multiItemId,
            status: 'complete',
          })
        )
        // delay 300ms for modal rendered
        setTimeout(
          () => itemMultiCompletionModalRef.current?.playConfetti(),
          300
        )
      })
      .catch(error => {
        setOpenConfirm(false)
        showError(error)
      })
  }, [
    onGetItemMulti,
    showError,
    router,
    generatePath,
    routerPages.QRcodeGetItemMulti,
    multiItemId,
  ])

  return (
    <CellFrame>
      <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
        <Stack sx={{ py: 4, bgcolor: 'grey.50' }} alignItems="center">
          <Button
            size="large"
            color="primary"
            sx={{ minWidth: 244 }}
            onClick={onClick}
            variant={variant}
            disabled={disabled || gettingItemMulti}
          >
            {label}
          </Button>
        </Stack>
      </BottomSafeArea>
      <InformationModal
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title={t('confirmAcquireItemTitle')}
        content=""
        cancelText={t('cancel')}
        buttons={
          <Button
            color="primary"
            sx={{ minWidth: 212 }}
            size="large"
            loading={gettingItemMulti}
            disabled={gettingItemMulti}
            onClick={handleAcquireItem}
          >
            {t('earn')}
          </Button>
        }
      />
    </CellFrame>
  )
}
